import React, { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import classNames from 'classnames';
import { WeekHourlyCount } from '@/model/Analytics';
import { useLocale } from '@/hook';
import Tooltip from '@/component/Tooltip';


const WEEK_DAY_TYPES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;

type WeekDayType = typeof WEEK_DAY_TYPES[number];

const countDaysPerWeekday = (start: Moment, end: Moment): Record<WeekDayType, number> => {
    const totalDays = end.diff(start, 'days') + 1;
    const fullWeeks = Math.floor(totalDays / 7);
    const counts = Object.fromEntries(WEEK_DAY_TYPES.map(day => [day, fullWeeks]));

    let currentDay = start.day();
    Array.from({ length: totalDays % 7 }).forEach(() => {
        counts[WEEK_DAY_TYPES[currentDay]] += 1;
        currentDay = (currentDay + 1) % 7;
    });
    return counts as Record<WeekDayType, number>;
};

/**
  * return (0 is lowest, classNumber - 1 is highest)
*/
const rankByPercentile = (values: number[], percentileBreaks: number[]): ({ value: number, rank: number })[] => {
    const total = values.length;
    return (
        values
            .map((value, index) => ({ value, originalIdx: index }))
            .sort((a, b) => a.value - b.value)
            .map((item, index) => {
                if (item.value === 0) {
                    return { ...item, rank: 0 };
                }

                const percentile = (index / (total - 1)) * 100;
                let rank = percentileBreaks.findIndex(b => percentile <= b);
                if (rank === -1) {
                    rank = percentileBreaks.length;
                }
                return { ...item, rank };
            })
            .sort((a, b) => a.originalIdx - b.originalIdx)
            .map(({ value, rank }) => ({ value, rank }))
        );
};


interface WeekyHourlyTableProps {
    period: { start: Moment, end: Moment };
    counts: WeekHourlyCount[];
    colorClasses: string[];
    breakPoints: number[];
    emptyColorClass?: string;
}

const WeekyHourlyTable: React.FC<WeekyHourlyTableProps> = (props) => {
    const { period, counts, colorClasses, breakPoints, emptyColorClass } = props;

    const intl = useIntl();
    const { locale } = useLocale();

    const weekHourlyMessageCount = useMemo(() => {
        const weekdayCounts = countDaysPerWeekday(period.start, period.end);

        const messageAvgs = Object.fromEntries(WEEK_DAY_TYPES.map(day => [day, Array(24).fill(0)]));
        counts.forEach(({ _id, count }) => {
            const [weekDay, hourStr] = _id.split("-") as [WeekDayType, string];
            const hour = parseInt(hourStr);
            messageAvgs[weekDay][hour] = (count / weekdayCounts[weekDay]);
        });
    
        return Object.fromEntries(
            Object.entries(messageAvgs)
                .map(([day, counts]) => [day, rankByPercentile(counts, breakPoints)])
        ) as Record<WeekDayType, { value: number, rank: number }[]>;
    }, [counts, period, breakPoints]);

    const isEmptyDay = useMemo(() => {
        return Object.fromEntries(
            Object.entries(weekHourlyMessageCount)
                .map(([day, counts]) => (
                    [day, counts.every(({ value }) => value === 0)]
                )
            )
        );
    }, [weekHourlyMessageCount]);

    const gridLabel = intl.formatMessage({ id: "i01009" });
    return (
        <div className="size-full grid grid-flow-col grid-rows-[24px_repeat(24,10px)] grid-cols-[36px_repeat(7,1fr)] gap-x-1 gap-y-0.5"
             role="grid"
             aria-label={gridLabel}
        >
            <>
                <div />
                {Array.from({ length: 8 }, (_, i) => i * 3).map(hour => (
                    <div key={hour} className="pr-1.5 row-span-3 text-center text-black-500 text-[14px] font-[500] leading-none">
                        {intl.formatMessage({ id: "i01017" }, { hour })}
                    </div>
                ))}
            </>

            {WEEK_DAY_TYPES.map((day, index) => (
                <Fragment key={day}>
                    <div className="pb-2 text-center text-black-500 text-[14px] font-[500] leading-none">
                        {moment.localeData(locale).weekdaysShort()[index]}
                    </div>
                    {weekHourlyMessageCount[day].map(({ value, rank }, hour) => (
                        <Tooltip key={hour}
                                 placement="top"
                                 offset={2}
                                 message={value.toFixed(1)}
                        >
                            <div tabIndex={isEmptyDay[day] ? -1 : 0}
                                 aria-label={intl.formatMessage({ id: "i01017" }, { hour })}
                                 className={classNames(
                                     "size-full",
                                     isEmptyDay[day] ? emptyColorClass : colorClasses[rank]
                                 )}
                            />
                        </Tooltip>
                    ))}
                </Fragment>
            ))}
        </div>
    );
};

export default WeekyHourlyTable;

